import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Property, PropertyInterface } from 'models/property.model';
import { BehaviorSubject, Observable } from 'rxjs';

export interface CreativeTemplateInterface {
  headline: string;
  description: string;
  subheadline: string;
  image: string;
  link: string;
}


@Injectable({
  providedIn: 'root'
})
export class PropertiesService {
  private approvedARecords: Array<string> = [
    "206.189.237.232"
  ];

  private displayWarningLabel: string;
  private displayWarning: boolean = false;

  private messageSource = new BehaviorSubject('none');
  public currentMessage: Observable<string>;
  
  public working_spinner: boolean = false;
  private _loaded: boolean = false;

  public activatePage: PropertyPageEnum = PropertyPageEnum.DASHBOARD;

  private _currentProperty: Property;
  private _properties: Array<Property>;
  
  get current(): Property {
    return this._currentProperty;
  }

  get loadedProperties(): boolean {
    return this._loaded;
  }

  get noProperties(): boolean {
    return this._properties && this._properties.length === 0 && this._loaded;
  } 

  get properties(): Array<Property> {
    return this._properties;
  }

  set currentProperty(property: Property) {
    this._currentProperty = property;
    this.messageSource.next("reload-icons");
    this.messageSource.next("reload-preview");
  }

  private eventEmitter: EventEmitter<any> = new EventEmitter();

  constructor(private http: HttpClient) {
    this.currentMessage = this.messageSource.asObservable();
  }

  public async setBlockedCountries(property: Property, hostname: string, blockedCountries: Array<string>) {
    const SET_BLOCKED_COUNTRIES_URL: string = `https://us-central1-relayclients.cloudfunctions.net/setBlockedCountries?hostname=${hostname.replace('.', '(dot)')}`;
    
    this.working_spinner = true;
    const blockedCountriesReply = await this.http.post(SET_BLOCKED_COUNTRIES_URL, {blockedCountries}).toPromise();
    console.log("Blocked Countries reply", blockedCountriesReply);
    property.configuration.configuration.blockedCountries = blockedCountries;
    this.messageSource.next("reload-icons");
    this.messageSource.next("reload-preview");
    this.working_spinner = false;
  }

  public async checkDomain(domainName: string): Promise<boolean> {
    const CHECK_DOMAIN_URL: string = `https://us-central1-relayclients.cloudfunctions.net/checkDomainName?domainName=${domainName}`;
    const checkDomainResponse: any = await this.http.get(CHECK_DOMAIN_URL).toPromise();
    console.log("Create property response", checkDomainResponse);
    return checkDomainResponse.data && checkDomainResponse.data.available;
  }

  public async registerDomain(domainName: string, userId: string): Promise<boolean> {
    const REGISTER_DOMAIN_URL: string = `https://us-central1-relayclients.cloudfunctions.net/registerNewDomain?domainName=${domainName}&user=${userId}`;
    const registerDomainResponse: any = await this.http.get(REGISTER_DOMAIN_URL).toPromise();
    console.log("Create property response", registerDomainResponse);
    return registerDomainResponse.status && registerDomainResponse.status === 'success';
  }

  public async createProperty(userId: string, property: Property): Promise<boolean> {
    const CREATE_PROPERTY_URL: string = `https://us-central1-relayclients.cloudfunctions.net/addUpdateProperty?userId=${userId}`;
    
    this.working_spinner = true;
    const newPropertyResponse = await this.http.post(CREATE_PROPERTY_URL, property).toPromise();
    console.log("Create property response", newPropertyResponse);
    this._properties.push(property);
    this.messageSource.next("reload-icons");
    this.messageSource.next("reload-preview");
    this._currentProperty = property;
    this.working_spinner = false;
    return false;
  }

  public async setTemplateHTML(property: Property, hostname: string, creative: CreativeTemplateInterface, trackingCodes?: string, pixelCode?: string) {
    const SET_TEMPLATE_HTML: string = `https://us-central1-relayclients.cloudfunctions.net/createNewTemplateFromVariables?hostname=${hostname.replace('.', '(dot)')}`;
    
    this.working_spinner = true;
    const newHtmlResponse = await this.http.post(SET_TEMPLATE_HTML, {hostname: property.hostname, template_id: property.template._id, trackingCodes: trackingCodes, pixelCode: pixelCode, creative: creative}).toPromise();
    console.log("Set HTML reply", newHtmlResponse);
    this.messageSource.next("reload-icons");
    this.messageSource.next("reload-preview");
    this.working_spinner = false;
  }

  public sanitizeDirtyUrl = (url: string, https?: boolean): string => {
    let returnValue: string = '';

    //Check if we have http://, or https://
    if (url.startsWith('http://') || url.startsWith('https://')) {
      //Fuck it, ship it 
      returnValue = url;
    } else {
      returnValue = https ? 'https://' + url : 'http://' + url;
    }

    return returnValue;
  }

  public async createSubdomainURL(prefix: string) {
    const CREATE_DNS_PREFIX_URL: string = `https://us-central1-relayclients.cloudfunctions.net/generateTestingUrl?username=${prefix}`;
    
    const dnsPrefixReply = await this.http.get(CREATE_DNS_PREFIX_URL).toPromise();
    console.log(dnsPrefixReply);
  }

  public async setDeadbolt(property: Property, hostname: string, deadbolt: string) {
    property.configuration.options.deadbolt = deadbolt;
    await this.updatePropertyConfiguration(property);
  }

  public async setFacebook(property: Property, facebook: string) {
    property.configuration.options.facebook = facebook;
    await this.updatePropertyConfiguration(property);
  }
 
  public async updatePropertyConfiguration(property: Property) {
    const UPDATE_PROPERTY: string = `https://us-central1-relayclients.cloudfunctions.net/updatePropertyConfiguration?hostname=${property.dirty_url.replace('.', '(dot)')}`;
    
    this.working_spinner = true;
    const propertyUpdate = await this.http.post(UPDATE_PROPERTY, property.configuration).toPromise();
    console.log("propertyUpdate reply", propertyUpdate);
    
    this.messageSource.next("reload-icons");
    this.messageSource.next("reload-preview");
    this.working_spinner = false;
  }
 
  public async getURLMetadata(url: string) {
    console.log(`getURLMetadata for ${url}`);
    
    const GET_URL_METADATA: string = `https://us-central1-relayclients.cloudfunctions.net/getMetaDataForURL?queryUrl=${url}`;
    const metadataResponse: any = await this.http.get(GET_URL_METADATA).toPromise();
    console.log(metadataResponse);
  }
  
  public async getDNS(url: string) {
    const GET_URL_DNS: string = `https://us-central1-relayclients.cloudfunctions.net/dnsLookup?queryUrl=${url}`;
    const dnsResponse: any = await this.http.get(GET_URL_DNS).toPromise();
    console.log(dnsResponse);
    if (dnsResponse && dnsResponse.address && !this.approvedARecords.includes(dnsResponse.address)) {
      //Set alert.
      this.displayWarning = true;
      this.displayWarningLabel = `${url} is not setup correctly, your DNS A record is currently "${dnsResponse.address}". Please point your DNS A Record to "206.189.237.232" or our servers won't be able to cloak your domain!`;
    }
  }


  public async getProperties(user_id: string) {
    
    const GET_PROPERTIES_URL: string = `https://us-central1-relayclients.cloudfunctions.net/getPropertiesForClient?clientId=${user_id}`;
    this.working_spinner = true;
    console.log(`Querying API for properties ${GET_PROPERTIES_URL}`);
    const propertiesResponse: Array<PropertyInterface> = <Array<PropertyInterface>> await this.http.get(GET_PROPERTIES_URL).toPromise();
    this._loaded = true;
    this._properties = propertiesResponse.map((property: PropertyInterface) => new Property(property));
    
    if (this.properties && this.properties.length > 0) {
      this._currentProperty = this._properties[0];
      this.messageSource.next("reload-icons");
      this.messageSource.next("properties-loaded");
    }
    this.working_spinner = false;
  }
}

export enum PropertyPageEnum {
  DASHBOARD = 0,
  FILTER = 1,
  REQUEST_LOGS = 2,
  CONFIG = 3
}